import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  EmailValidator,
  FormControlComponent,
  InPostPointLocation,
  InPostPointSelect,
  InPostPointSelectComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { InputText } from 'primeng/inputtext';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import {
  executeOrNull,
  Guid,
  isNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import {
  defer,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  OrderNumber,
  PublicOrderShippingAddressForm,
  PublicOrderShippingAddressFormGroup,
  PublicOrderShippingMethodCollectionItem,
} from '@web/shared/module/shop';
import {
  PhoneNumberWithPrefix,
  PhoneNumberWithPrefixComponent,
  PublicDictionaryCountrySelectItemsProviderService,
} from '@web/shared/module/dictionary';
import { PublicOrderShippingMethodSelectItemsProviderService } from '../../providers/public-order-shipping-method-select-items-provider.service';

@Component({
  selector: 'app-public-order-shipping-address-form',
  imports: [
    CommonModule,
    Button,
    FormControlComponent,
    InputText,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    ReactiveFormsModule,
    PrimengSelectDirective,
    Select,
    SpinnerFilledComponent,
    InPostPointSelectComponent,
    PhoneNumberWithPrefixComponent,
  ],
  templateUrl: './public-order-shipping-address-form.component.html',
  styleUrl: './public-order-shipping-address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicOrderShippingAddressFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly availableShippingMethodSelectItemsProvider = inject(PublicOrderShippingMethodSelectItemsProviderService);
  /* prettier-ignore */
  private readonly countrySelectItemsProvider = inject(PublicDictionaryCountrySelectItemsProviderService);

  public readonly orderNumber = input.required<OrderNumber>();
  public readonly shipmentId = input.required<Guid>();
  /* prettier-ignore */
  public readonly data = input<Nillable<PublicOrderShippingAddressForm>>();
  /* prettier-ignore */
  public readonly submitClickEvent = output<PublicOrderShippingAddressForm>();
  public readonly cancelClickEvent = output<void>();
  public readonly pending = input<boolean>(false);

  protected readonly shippingMethodSelectItems$: Observable<SelectItem<Guid>[]>;
  protected readonly currentCountryCode$: Observable<Nillable<string>>;
  protected readonly shippingMethodHint$: Observable<Nillable<string>>;
  protected readonly stateFormControlAvailability$!: Observable<boolean>;
  protected readonly countrySelectItems$!: Observable<SelectItem<Guid>[]>;
  protected readonly form!: PublicOrderShippingAddressFormGroup;
  /* prettier-ignore */
  protected readonly currentShippingMethod$: Observable<Nillable<PublicOrderShippingMethodCollectionItem>>;
  protected readonly valueOrDefault = valueOrDefault;

  public constructor() {
    this.form = this.buildForm();
    this.countrySelectItems$ = this.prepareCountrySelectItemsSource();
    this.stateFormControlAvailability$ = this.prepareStateAvailabilitySource();
    this.currentCountryCode$ = this.prepareCurrentCountryCodeSource();
    /* prettier-ignore */
    this.shippingMethodHint$ = this.prepareShippingMethodHintSource(this.currentCountryCode$);
    this.shippingMethodSelectItems$ = this.prepareShippingMethodSelectItems();
    this.currentShippingMethod$ = this.prepareCurrentShippingMethodSource();
  }

  public ngOnInit(): void {
    this.fillForm();
  }

  public handleSubmitButtonClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  public handleCancelButtonClick(): void {
    this.cancelClickEvent.emit();
  }

  private toData(): PublicOrderShippingAddressForm {
    const data = this.form.getRawValue();

    return new PublicOrderShippingAddressForm({
      city: data.city,
      companyName: data.companyName,
      countryId: data.countryId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      postCode: data.postCode,
      state: data.state,
      streetLine1: data.streetLine1,
      streetLine2: data.streetLine2,
      shippingMethodId: data.shippingMethodId,
      shippingPoint: data.shippingPoint,
    });
  }

  private buildForm(): PublicOrderShippingAddressFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      city: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicOrderShippingAddressForm.CityMaxLength)]),
      companyName: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicOrderShippingAddressForm.CompanyNameMaxLength)]),
      countryId: this.builder.control<Nillable<Guid>>({value: null, disabled: true}, [Validators.required]),
      email: this.builder.control<Nillable<string>>(null, [Validators.required, EmailValidator.email]),
      firstName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicOrderShippingAddressForm.FirstNameMaxLength)]),
      lastName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicOrderShippingAddressForm.LastNameMaxLength)]),
      phone: this.builder.control<Nillable<PhoneNumberWithPrefix>>(null, [Validators.required]),
      postCode: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicOrderShippingAddressForm.PostCodeMaxLength)]),
      state: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicOrderShippingAddressForm.StateMaxLength)]),
      streetLine1: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(PublicOrderShippingAddressForm.StreetMaxLength)]),
      streetLine2: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicOrderShippingAddressForm.StreetMaxLength)]),
      shippingMethodId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      shippingPoint: this.builder.control<Nillable<InPostPointSelect>>(null, [Validators.required]),
    });
  }

  private fillForm(): void {
    const data = this.data();

    this.form.patchValue({
      city: data?.city,
      companyName: data?.companyName,
      countryId: data?.countryId,
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      phone: data?.phone,
      postCode: data?.postCode,
      state: data?.state,
      streetLine1: data?.streetLine1,
      streetLine2: data?.streetLine2,
      shippingMethodId: data?.shippingMethodId,
      shippingPoint: data?.shippingPoint,
    });
  }

  private prepareCountrySelectItemsSource(): Observable<SelectItem<Guid>[]> {
    return this.countrySelectItemsProvider.fetchSelectItems('id');
  }

  private prepareStateAvailabilitySource(): Observable<boolean> {
    const { countryId, state } = this.form.controls;

    return countryId.valueChanges.pipe(
      startWith(countryId.value),
      switchMap((countryId) =>
        isNil(countryId)
          ? of(null)
          : this.countrySelectItemsProvider.fetchIsoCodeForId(countryId),
      ),
      map((code) => code !== 'PL' && !isNil(code)),
      tap((isAvailable) => (isAvailable ? state.enable() : state.disable())),
    );
  }

  private prepareCurrentCountryCodeSource(): Observable<Nillable<string>> {
    return defer(() =>
      this.form.controls.countryId.valueChanges.pipe(
        startWith(this.form.controls.countryId.value),
        distinctUntilChanged(),
        switchMap((countryId) => {
          return isNil(countryId)
            ? of(null)
            : this.countrySelectItemsProvider.fetchIsoCodeForId(countryId);
        }),
      ),
    );
  }

  /* prettier-ignore */
  private prepareShippingMethodHintSource(currentCountryCode$: Observable<Nillable<string>>): Observable<Nillable<string>> {
    return defer(()=> currentCountryCode$.pipe(
      map((code) => !isNil(code)),
      tap((availability) => {
        const { shippingMethodId } = this.form.controls;

        availability
          ? shippingMethodId.enable({ emitEvent: false })
          : shippingMethodId.disable({ emitEvent: false });
      }),
      map((availability) => availability ? null : 'Aby wybrać rodzaj wysyłki, należy najpierw wskazać kraj.'),
    ));
  }

  private prepareShippingMethodSelectItems(): Observable<SelectItem<Guid>[]> {
    return defer(() =>
      this.form.controls.countryId.valueChanges.pipe(
        tap(() => {
          this.form.controls.shippingMethodId.setValue(null);
        }),
        startWith(this.form.controls.countryId.value),
        distinctUntilChanged(),
        switchMap((countryId) =>
          isNil(countryId)
            ? of([])
            : this.availableShippingMethodSelectItemsProvider.fetchSelectItems({
                orderNumber: this.orderNumber(),
                shipmentId: this.shipmentId(),
                key: 'id',
                countryId,
              }),
        ),
      ),
    );
  }

  private prepareCurrentShippingMethodSource(): Observable<
    Nillable<PublicOrderShippingMethodCollectionItem>
  > {
    return defer(() =>
      this.form.controls.shippingMethodId.valueChanges.pipe(
        startWith(this.form.controls.shippingMethodId.value),
        switchMap((id) =>
          isNil(id)
            ? of(null)
            : this.availableShippingMethodSelectItemsProvider.fetchShippingMethodById(
                {
                  id,
                  countryId: this.form.controls.countryId.value,
                  orderNumber: this.orderNumber(),
                  shipmentId: this.shipmentId(),
                },
              ),
        ),
        tap((method) => {
          const { shippingPoint } = this.form.controls;

          if (isNil(method)) {
            shippingPoint.disable({ emitEvent: false });

            return;
          }

          if (
            [
              method.isDomesticInPostGeowidget,
              method.isInternationalInPostGeowidget,
            ].some((result) => result)
          ) {
            shippingPoint.enable({ emitEvent: false });

            shippingPoint.setValue(
              executeOrNull(
                method.inPostGeowidgetProperty,
                (property) =>
                  new InPostPointSelect({
                    name: property.value,
                    description: property.description,
                    location: new InPostPointLocation({
                      longitude: property.longitude!,
                      latitude: property.latitude!,
                    }),
                  }),
              ),
            );

            return;
          }

          shippingPoint.disable({ emitEvent: false });
        }),
      ),
    );
  }
}
